import { useState, useEffect, useCallback } from 'react';
import { createApi } from '../services/api.js';

interface TeamMember {
  user_id: string;
  email: string;
  role: string;
  joined_at: string;
}

interface TeamData {
  id: string;
  name: string;
  role: string;
  member_count: number;
  members?: TeamMember[];
}

export function TeamPage({ token }: { token: string }) {
  const [team, setTeam] = useState<TeamData | null>(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [teamName, setTeamName] = useState('');
  const [inviteLink, setInviteLink] = useState<string | null>(null);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteCopied, setInviteCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const api = createApi(token);

  useEffect(() => {
    api.getTeam().then((res) => {
      if (res.ok && res.data) {
        // API returns team object directly (or null if no team)
        const t = res.data;
        if (t && t.id) {
          setTeam({
            id: t.id,
            name: t.name,
            role: t.my_role ?? 'member',
            member_count: t.seats_used ?? t.members?.length ?? 0,
            members: t.members?.map((m: any) => ({
              user_id: m.user_id,
              email: m.email ?? m.user_id.slice(0, 8),
              role: m.role,
              joined_at: m.joined_at,
            })),
          });
        }
      }
      setLoading(false);
    });
  }, [token]);

  const handleCreate = useCallback(async () => {
    if (!teamName.trim()) return;
    setCreating(true);
    setError(null);
    const res = await api.createTeam(teamName.trim());
    if (res.ok) {
      // Reload team data
      const teamRes = await api.getTeam();
      if (teamRes.ok && teamRes.data) {
        const t = teamRes.data;
        if (t && t.id) {
          setTeam({
            id: t.id,
            name: t.name,
            role: t.my_role ?? 'member',
            member_count: t.seats_used ?? t.members?.length ?? 0,
            members: t.members?.map((m: any) => ({
              user_id: m.user_id,
              email: m.email ?? m.user_id.slice(0, 8),
              role: m.role,
              joined_at: m.joined_at,
            })),
          });
        }
      }
    } else {
      setError(res.error.message);
    }
    setCreating(false);
  }, [api, teamName]);

  const handleGenerateInvite = useCallback(async () => {
    setInviteLoading(true);
    const res = await api.generateInviteLink();
    if (res.ok) {
      setInviteLink(res.data.invite_url);
    } else {
      setError(res.error.message);
    }
    setInviteLoading(false);
  }, [api]);

  const handleCopyInvite = useCallback(() => {
    if (!inviteLink) return;
    navigator.clipboard.writeText(inviteLink);
    setInviteCopied(true);
    setTimeout(() => setInviteCopied(false), 2000);
  }, [inviteLink]);

  if (loading) {
    return (
      <div>
        <h1 className="text-xl font-bold text-ink mb-6">Team</h1>
        <div className="rounded-xl border border-surface-200 bg-white p-6 animate-pulse">
          <div className="h-5 w-32 bg-surface-100 rounded mb-4" />
          <div className="h-4 w-48 bg-surface-100 rounded" />
        </div>
      </div>
    );
  }

  if (!team) {
    return (
      <div>
        <h1 className="text-xl font-bold text-ink mb-6">Team</h1>
        <div className="rounded-xl border border-surface-200 bg-white p-8 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-brand/10 text-brand">
            <svg viewBox="0 0 24 24" fill="none" className="h-6 w-6">
              <path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M22 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-ink">Create a team</h2>
          <p className="mt-2 text-sm text-ink-muted max-w-sm mx-auto">
            Teams of 3+ members get discounted pricing: Fortress at EUR 7/mo, Citadel at EUR 15/mo.
          </p>
          <div className="mt-6 flex items-center gap-2 max-w-xs mx-auto">
            <input
              type="text"
              placeholder="Team name"
              value={teamName}
              onChange={(e) => setTeamName(e.target.value)}
              className="flex-1 rounded-lg border border-surface-200 px-3 py-2.5 text-sm text-ink placeholder:text-ink-light focus:border-brand focus:outline-none"
              onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
            />
            <button
              onClick={handleCreate}
              disabled={creating || !teamName.trim()}
              className="shrink-0 rounded-lg bg-brand px-5 py-2.5 text-sm font-semibold text-white hover:bg-brand-dark transition disabled:opacity-50"
            >
              {creating ? 'Creating...' : 'Create'}
            </button>
          </div>
          {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
        </div>
      </div>
    );
  }

  const isAdmin = team.role === 'owner' || team.role === 'admin';
  const isOwner = team.role === 'owner';
  const discountActive = team.member_count >= 3;

  async function handleLeave() {
    if (!confirm('Are you sure you want to leave this team?')) return;
    setError(null);
    const res = await api.leaveTeam();
    if (res.ok) {
      setTeam(null); // back to "create team" view
    } else {
      setError(res.error.message);
    }
  }

  async function handleRemoveMember(userId: string, email: string) {
    if (!confirm(`Remove ${email} from the team?`)) return;
    setError(null);
    const res = await api.removeTeamMember(userId);
    if (res.ok) {
      // Update local state
      setTeam(prev => prev ? {
        ...prev,
        member_count: prev.member_count - 1,
        members: prev.members?.filter(m => m.user_id !== userId),
      } : null);
    } else {
      setError(res.error.message);
    }
  }

  return (
    <div>
      <h1 className="text-xl font-bold text-ink mb-6">Team</h1>

      {/* Team info */}
      <div className="rounded-xl border border-surface-200 bg-white p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-ink">{team.name}</h2>
            <p className="text-sm text-ink-muted">
              {team.member_count} member{team.member_count !== 1 ? 's' : ''}
              {' '}&middot;{' '}
              Your role: <span className="font-medium capitalize">{team.role}</span>
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className={`rounded-full px-3 py-1 text-xs font-semibold ${
              discountActive
                ? 'bg-green-50 text-green-700 border border-green-200'
                : 'bg-amber-50 text-amber-700 border border-amber-200'
            }`}>
              {discountActive ? 'Discount active' : `${3 - team.member_count} more for discount`}
            </div>
            {!isOwner && (
              <button
                onClick={handleLeave}
                className="rounded-full px-3 py-1 text-xs font-medium text-red-600 border border-red-200 hover:bg-red-50 transition"
              >
                Leave team
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Members list */}
      {team.members && team.members.length > 0 && (
        <div className="mt-4 rounded-xl border border-surface-200 bg-white overflow-hidden">
          <div className="px-6 py-3 border-b border-surface-100">
            <h3 className="text-sm font-semibold text-ink">Members</h3>
          </div>
          <ul className="divide-y divide-surface-100">
            {team.members.map((m) => (
              <li key={m.user_id} className="flex items-center justify-between px-6 py-3">
                <div>
                  <p className="text-sm font-medium text-ink">{m.email}</p>
                  <p className="text-xs text-ink-light">
                    Joined {new Date(m.joined_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-medium capitalize px-2 py-0.5 rounded ${
                    m.role === 'owner' ? 'bg-brand/10 text-brand' : 'bg-surface-100 text-ink-muted'
                  }`}>
                    {m.role}
                  </span>
                  {isAdmin && m.role !== 'owner' && m.user_id !== team.id && (
                    <button
                      onClick={() => handleRemoveMember(m.user_id, m.email)}
                      className="text-xs text-red-500 hover:text-red-700 transition"
                      title={`Remove ${m.email}`}
                    >
                      Remove
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Invite section */}
      {isAdmin && (
        <div className="mt-4 rounded-xl border border-surface-200 bg-white p-6">
          <h3 className="text-sm font-semibold text-ink mb-2">Invite members</h3>
          <p className="text-sm text-ink-muted mb-4">
            Share this link with colleagues. They'll need to sign in and can then join your team.
          </p>
          {inviteLink ? (
            <div className="flex items-center gap-2">
              <input
                readOnly
                value={inviteLink}
                className="flex-1 rounded-lg border border-surface-200 bg-surface-50 px-3 py-2 text-xs text-ink-muted font-mono truncate"
                onClick={(e) => (e.target as HTMLInputElement).select()}
              />
              <button
                onClick={handleCopyInvite}
                className="shrink-0 rounded-lg bg-brand px-4 py-2 text-xs font-semibold text-white hover:bg-brand-dark transition"
              >
                {inviteCopied ? 'Copied!' : 'Copy'}
              </button>
            </div>
          ) : (
            <button
              onClick={handleGenerateInvite}
              disabled={inviteLoading}
              className="rounded-lg bg-surface-100 px-4 py-2 text-sm font-semibold text-ink hover:bg-surface-200 transition disabled:opacity-50"
            >
              {inviteLoading ? 'Generating...' : 'Generate invite link'}
            </button>
          )}
          {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
        </div>
      )}
    </div>
  );
}
